import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import RecipeCard from "@/components/RecipeCard";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { Search, X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export default function Browse() {
  const { user } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/recipes").then((r) => setRecipes(r.data)).finally(() => setLoading(false));
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return recipes;
    return recipes.filter((r) => {
      const inTitle = (r.title || "").toLowerCase().includes(q);
      const inDesc = (r.description || "").toLowerCase().includes(q);
      const inTags = (r.tags || []).some((t) => (t || "").toLowerCase().includes(q));
      return inTitle || inDesc || inTags;
    });
  }, [recipes, query]);

  return (
    <div className="nerd-gradient min-h-screen">
      <section className="pt-8 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-6">All recipes</h1>

          <div className="relative max-w-xl mb-8">
            <Search className="w-5 h-5 text-nerd-light/40 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              data-testid="browse-search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search recipes, ingredients, tags…"
              className="w-full bg-nerd-card border border-white/10 rounded-full pl-12 pr-11 py-3.5 text-nerd-light placeholder:text-nerd-light/40 focus:outline-none focus:border-white/30 transition-colors"
            />
            {query && (
              <button
                data-testid="browse-search-clear"
                onClick={() => setQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-nerd-light/40 hover:text-nerd-light transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-0">
            {Array.from({ length: 18 }).map((_, i) => (
              <Skeleton key={i} className="aspect-[9/16] rounded-none bg-nerd-card" />
            ))}
          </div>
        ) : results.length ? (
          <div data-testid="browse-grid" className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-0">
            {results.map((r, i) => (
              <RecipeCard key={r.id} recipe={r} index={i} flat />
            ))}
          </div>
        ) : (
          <div data-testid="browse-empty" className="py-20 text-center text-nerd-light/40 text-sm">
            No recipes match “{query}”.
          </div>
        )}
      </section>
      {!user && <Footer />}
    </div>
  );
}
